import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import {
  isLoading,
  LoadingTypes,
  PartnerType,
  useAccountId,
  useTenantPartnerId,
  useUserType,
} from '@labrav/common'
import { isEmpty } from 'lodash'
import { useDispatch, useSelector } from '../../store'
import {
  getFlyoutListingData,
  getFlyoutListingDataMicrosoft,
} from '../../modules/flyOutSyncs/action'
import {
  getFlyoutMicrosoftOnboardingFormData,
  getFlyOutOnboardingData,
} from '../../modules/flyOutOnboarding/action'
import { CloudType } from '../../types'
import type {
  FlyOutListingData,
  MicrosoftFlyOutListingData,
} from '../../types/flyout'
import type { AccountData } from '../../types/external'

type SubscribedFlyoutProduct = {
  cloudProviders?: string[]
}

export const useSellerInfo = () => {
  const tenantPartnerId = useTenantPartnerId()
  const { pathname } = useLocation()
  const isAdmin = useUserType() === PartnerType.Admin
  const {
    partnerData,
    flyoutSellerInfo,
    isLoadingSeller,
    hasFlyoutOnboardingData,
    microsoftSellerInfo,
    hasMicrosoftOnboardingData,
  } = useSelector<{
    flyoutSellerInfo: FlyOutListingData
    partnerData: AccountData | undefined
    isLoadingSeller: boolean
    hasFlyoutOnboardingData: boolean
    microsoftSellerInfo: MicrosoftFlyOutListingData | undefined
    hasMicrosoftOnboardingData: boolean
  }>(state => ({
    flyoutSellerInfo: state.flyoutSellerInfo,
    partnerData: state.AccountData.user.partnerData || undefined,
    isLoadingSeller:
      isLoading(state.loading)(LoadingTypes.MARKETPLACE_INFO) ||
      isLoading(state.loading)(LoadingTypes.FLYOUT_ONBOARDING_DATA) ||
      isLoading(state.loading)(LoadingTypes.MARKETPLACE_INFO_MICROSOFT),
    hasFlyoutOnboardingData: !isEmpty(state.flyOutOnboarding.onboardingForm),
    microsoftSellerInfo: state.flyOutSellerInfoMicrosoft,
    hasMicrosoftOnboardingData:
      state.flyOutOnboarding.microsoftOnboardingForm.submit,
  }))
  const flyoutProduct = (
    partnerData?.subscribedProducts as
      | { FLYOUT?: SubscribedFlyoutProduct }
      | undefined
  )?.FLYOUT
  const flyoutSubscribedCloudProviders = flyoutProduct?.cloudProviders || []
  const activeProducts = partnerData?.activeProducts as string[] | undefined
  const activeProductsWithCloud = partnerData?.activeProductsWithCloud as
    | string[]
    | undefined
  const dispatch = useDispatch()

  const accountIdAWS = useAccountId({ product: 'flyout', cloud: CloudType.AWS })
  const accountIdMicrosoft = useAccountId({
    product: 'flyout',
    cloud: CloudType.AZURE,
  })

  const isOnboardingRoute = pathname.includes('/onboarding')
  const isAWSSettingsRoute =
    pathname.includes('/settings/cloud/aws') ||
    (pathname.includes('/cloud/aws') && !isOnboardingRoute)

  useEffect(() => {
    if (
      accountIdAWS &&
      activeProducts &&
      activeProducts?.includes('flyout') &&
      flyoutSubscribedCloudProviders?.includes('AWS')
    ) {
      dispatch(
        getFlyoutListingData({
          partnerId: accountIdAWS,
          metadata: true,
        })
      )
    }
  }, [activeProducts, flyoutSubscribedCloudProviders, accountIdAWS])
  useEffect(() => {
    if (
      accountIdMicrosoft &&
      activeProducts &&
      activeProducts?.includes('flyout') &&
      activeProductsWithCloud?.includes('FLYOUT_AZURE') &&
      flyoutSubscribedCloudProviders?.includes('AZURE') &&
      !isAWSSettingsRoute
    ) {
      dispatch(
        getFlyoutListingDataMicrosoft({
          accountId: accountIdMicrosoft,
        })
      )
    }
  }, [
    partnerData?.partnerId,
    activeProducts,
    flyoutSubscribedCloudProviders,
    accountIdAWS,
    accountIdMicrosoft,
  ])
  const loading = Boolean(!partnerData || isLoadingSeller)

  const flyoutOnboardingFetchAttemptedRef = useRef(false)

  useEffect(() => {
    flyoutOnboardingFetchAttemptedRef.current = false
  }, [accountIdAWS, tenantPartnerId])

  useEffect(() => {
    if (
      accountIdAWS &&
      tenantPartnerId &&
      !flyoutSellerInfo?.onboarded &&
      !loading &&
      !hasFlyoutOnboardingData &&
      flyoutSubscribedCloudProviders?.includes('AWS') &&
      !isAWSSettingsRoute &&
      !isAdmin &&
      !flyoutOnboardingFetchAttemptedRef.current
    ) {
      flyoutOnboardingFetchAttemptedRef.current = true
      dispatch(
        getFlyOutOnboardingData(accountIdAWS, tenantPartnerId, CloudType.AWS)
      )
    }
  }, [
    accountIdAWS,
    tenantPartnerId,
    flyoutSellerInfo,
    loading,
    hasFlyoutOnboardingData,
    flyoutSubscribedCloudProviders,
    isAWSSettingsRoute,
    isAdmin,
  ])
  useEffect(() => {
    if (
      accountIdMicrosoft &&
      !microsoftSellerInfo?.onboarded &&
      !loading &&
      !hasMicrosoftOnboardingData &&
      activeProductsWithCloud?.includes('FLYOUT_AZURE') &&
      flyoutSubscribedCloudProviders?.includes('AZURE')
    ) {
      dispatch(
        getFlyoutMicrosoftOnboardingFormData(
          accountIdMicrosoft,
          CloudType.AZURE,
          false
        )
      )
    }
  }, [
    accountIdMicrosoft,
    microsoftSellerInfo,
    loading,
    flyoutSubscribedCloudProviders,
    hasMicrosoftOnboardingData,
  ])
  return { loading: loading, flyoutSellerInfo, microsoftSellerInfo }
}
